import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit2, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";

function TransactionList({ transactions, categories, onEdit, onDelete, currency }) {
  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    return category ? category.name : categoryId;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'income':
        return 'bg-success/10 text-success border-success/20';
      case 'expense':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'savings':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getAmountColor = (type) => {
    switch (type) {
      case 'income':
        return 'text-success';
      case 'expense':
        return 'text-destructive';
      default:
        return 'text-accent';
    }
  };

  if (transactions.length === 0) {
    return (
      <Card data-testid="transactions-list" className="border border-border">
        <CardHeader>
          <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
            Recent Transactions
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="text-center py-12 text-muted-foreground">
            <p>No transactions yet. Start by adding your first transaction!</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="transactions-list" className="border border-border">
      <CardHeader>
        <CardTitle className="text-xl sm:text-2xl tracking-tight font-semibold">
          Recent Transactions
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-2">
          {transactions.map((transaction) => (
            <div
              key={transaction.transaction_id}
              data-testid={`transaction-${transaction.transaction_id}`}
              className="flex items-center justify-between p-4 border border-border rounded-md transition-colors duration-200 hover:bg-slate-50"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="w-10 h-10 rounded-md bg-muted flex items-center justify-center text-sm font-semibold">
                  {getCategoryName(transaction.category).substring(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-semibold text-sm">{getCategoryName(transaction.category)}</p>
                    <Badge className={getTypeColor(transaction.type)}>
                      {transaction.type}
                    </Badge>
                  </div>
                  {transaction.description && (
                    <p className="text-xs text-muted-foreground truncate">
                      {transaction.description}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {format(new Date(transaction.date), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className={`text-lg tracking-tight font-bold ${getAmountColor(transaction.type)}`}>
                    {transaction.type === 'income' ? '+' : '-'}
                    {currency} {transaction.amount.toFixed(2)}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    data-testid={`edit-transaction-${transaction.transaction_id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(transaction)}
                    className="h-8 w-8 p-0"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        data-testid={`delete-transaction-${transaction.transaction_id}`}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete Transaction</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to delete this transaction? This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(transaction.transaction_id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default TransactionList;